import { wave4RoutePatterns } from "@lifescapsule/contracts";
import { lazy, Suspense, useEffect, useRef, useState } from "react";
import {
  Link,
  Navigate,
  NavLink,
  Route,
  Routes,
  useLocation,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { Icon } from "./shared/Icon";
import { Logo } from "./shared/Logo";
import { RouteMetadata } from "./shared/RouteMetadata";
import { ScrollToTop } from "./shared/ScrollToTop";
import { installInterfaceMotion } from "./shared/interface-motion";
import "./shared/interface-motion.css";
import { useAuthSession } from "./shared/AuthSession";
import { StudioSessionObservation } from "./shared/StudioSessionObservation";
import "./shared/customer-tokens.css";
import "./shared/customer-shell.css";
import ProductPage from "./features/catalog/ProductPage";
import HomePage from "./features/marketing/HomePage";
import NotFoundPage from "./features/marketing/NotFoundPage";
import {
  MakingTaskContext,
  PageChromeContext,
  publicSitePage,
  redesignOwnsPage,
  shellChrome,
  signedOutTaskLabel,
  type MakingTask,
} from "./features/next-ui/page-chrome";
import { MakingChrome } from "./features/next-ui/access/FoundationChrome";
import { PageLoading } from "./features/next-ui/shared/PageLoading";
import {
  ClassicBookSettingsRedirect,
  NextAccountSettingsPage,
  NextBookDetailsPage,
  NextBookFamilyPage,
  NextBookSettingsPage,
  NextCollectionPage,
  NextExampleHubPage,
  NextFaqPage,
  NextHowItWorksPage,
  NextLibraryPage,
  NextRedeemPage,
  NextStartPage,
  NextSupportApprovalPage,
  NextSupportPage,
  NextUnsubscribePage,
  NextRecoveryPage,
  NextRetiredCheckoutPage,
  NextSignInCompletePage,
  NextSignInPage,
  Redesign,
  RedesignRedirect,
  VoucherQuestionsRedirect,
  NextOrderProgressPage,
  NextPrintReviewPage,
} from "./features/next-ui/Redesign";
import { OneOrderPerPage } from "./features/fulfillment/order-route";
import { useUiMode } from "./features/next-ui/ui-mode";
import { legacyReadingHref } from "./features/next-ui/library/legacy-reading-link";

const CatalogPage = lazy(() => import("./features/catalog/CatalogPage"));
const DemoHubPage = lazy(() => import("./features/storefront/DemoHubPage"));
const MemoirDemoPage = lazy(() => import("./features/memoir/MemoirDemoPage"));
const HowItWorksPage = lazy(() => import("./features/marketing/HowItWorksPage"));
const BedtimeStoryDemoPage = lazy(() => import("./features/marketing/BedtimeStoryDemoPage"));
const QvcResumePage = lazy(() => import("./features/qvc/QvcResumePage"));
const PricingPage = lazy(() => import("./features/marketing/PricingPage"));
const FaqPage = lazy(() => import("./features/marketing/FaqPage"));
const PolicyPage = lazy(() => import("./features/marketing/PolicyPage"));
const SignInPage = lazy(() => import("./features/account/SignInPage"));
const MagicSignInPage = lazy(() => import("./features/account/MagicSignInPage"));
const AccountPage = lazy(() => import("./features/account/AccountPage"));
const BookCapabilityHubPage = lazy(() => import("./features/account/BookCapabilityHubPage"));
const CreateExampleBookPage = lazy(() => import("./features/account/CreateExampleBookPage"));
const AcceptInvitationPage = lazy(() => import("./features/account/AcceptInvitationPage"));
const SupportApprovalPage = lazy(() => import("./features/account/SupportApprovalPage"));
const UnsubscribeReminderPage = lazy(() => import("./features/account/UnsubscribeReminderPage"));
const ListenPage = lazy(() => import("./features/marketing/ListenPage"));
const CheckoutPage = lazy(() => import("./features/commerce/CheckoutPage"));
const CheckoutSuccessPage = lazy(() => import("./features/commerce/CheckoutSuccessPage"));
const VoucherPurchasePage = lazy(() => import("./features/commerce/VoucherPurchasePage"));
const RedeemPage = lazy(() => import("./features/commerce/RedeemPage"));
const StudioPage = lazy(() => import("./features/studio/StudioPage"));
const StudioFlagshipMock = lazy(() => import("./features/studio/StudioFlagshipMock"));
const StudioPocPage = import.meta.env.DEV
  ? lazy(() => import("./features/studio-poc/StudioPocPage"))
  : null;
const studioPocPath = import.meta.env.DEV ? "/app/studio-poc" : null;
const PipelineArtifactReviewLab = import.meta.env.DEV
  ? lazy(() => import("./features/studio/PipelineArtifactReviewLab"))
  : null;
const ArrangeRoute = lazy(() => import("./features/studio/ArrangeRoute"));
const NarrativeProductionPage = lazy(() => import("./features/production/NarrativeProductionPage"));
const PhysicalOrderPage = lazy(() => import("./features/fulfillment/PhysicalOrderPage"));
const ExactPrintReviewPage = lazy(() => import("./features/fulfillment/ExactPrintReviewPage"));
const GuidedBedtimePage = lazy(() => import("./features/guided/GuidedBedtimePage"));
const PhotoLibraryPage = lazy(() => import("./features/photos/PhotoLibraryPage"));
const StoryWorkspacePage = lazy(() => import("./features/story/StoryWorkspacePage"));
const ContactSupportPage = lazy(() => import("./features/support/ContactSupportPage"));

function PublicHeader() {
  const auth = useAuthSession();
  const [open, setOpen] = useState(false);
  const menuButton = useRef<HTMLButtonElement>(null);
  const close = () => setOpen(false);

  useEffect(() => {
    if (!open) return;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      setOpen(false);
      menuButton.current?.focus();
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [open]);

  return (
    <>
      <header className="site-header site-header--all-books">
        <div className="shell header-inner">
          <Logo light clean />
          <button
            ref={menuButton}
            className="menu-button"
            type="button"
            aria-expanded={open}
            aria-controls="primary-nav"
            onClick={() => setOpen(!open)}
          >
            <span className="sr-only">{open ? "Close" : "Open"} menu</span>
            <Icon name={open ? "close" : "menu"} size={24} />
          </button>
          <nav
            id="primary-nav"
            className={open ? "nav nav--open" : "nav"}
            aria-label="Primary navigation"
          >
            <NavLink to="/how-it-works" onClick={close}>
              How it works
            </NavLink>
            <NavLink className="nav__explorer" to="/books" onClick={close}>
              Explore books
            </NavLink>
            <NavLink to="/demo" onClick={close}>
              See examples
            </NavLink>
            <NavLink className="nav__signin" to={auth.tokens ? "/app" : "/sign-in"} onClick={close}>
              {auth.tokens ? "My Library" : "Sign in"}
            </NavLink>
            {/* Most visitors arrive holding a voucher code: using it is the header's main action. */}
            <Link className="button button--small" to="/redeem" onClick={close}>
              Use your voucher
            </Link>
          </nav>
        </div>
      </header>
    </>
  );
}

/** A signed-out task's own header: the brand and what this page is for (W3 Rounds 10–11). */
function TaskHeader({ label }: { label: string }) {
  return (
    <header className="commerce-header">
      <div className="shell commerce-header__inner">
        <Logo />
        <span>{label}</span>
      </div>
    </header>
  );
}

function Footer({ compact = false }: { compact?: boolean }) {
  if (compact) {
    return (
      <footer className="footer footer--lc93-compact">
        <div className="shell footer__lc93-row">
          <div className="footer__lc93-brand">
            <Logo light clean />
            <p>Family memories, made into books.</p>
          </div>
          <nav className="footer__lc93-primary" aria-label="Public footer">
            <Link to="/books">Books</Link>
            <Link to="/how-it-works">How it works</Link>
            <Link to="/faq">Help</Link>
            <Link to="/privacy">Privacy</Link>
            <Link to="/terms">Terms</Link>
          </nav>
          <details className="footer__lc93-more">
            <summary>More</summary>
            <div>
              <Link to="/pricing">Pricing</Link>
              <Link to="/redeem">Use your voucher</Link>
              <Link to="/accessibility">Accessibility</Link>
              <Link to="/ai-and-media-consent">Creative tools &amp; media</Link>
              <Link to="/refunds">Refunds</Link>
              <a href="mailto:lifescapsule@jpwlabs.com">Contact support</a>
            </div>
          </details>
        </div>
        <div className="shell footer__lc93-meta">
          <span>© {new Date().getFullYear()} Life’s Capsule</span>
          <span>Private family storytelling, made with care.</span>
        </div>
      </footer>
    );
  }

  return (
    <footer className="footer">
      <div className="shell footer__grid">
        <div>
          <Logo light />
          <p>Turn your memories into a book, with guidance at every step.</p>
        </div>
        <div>
          <h2>Explore</h2>
          <Link to="/books">The books</Link>
          <Link to="/how-it-works">How it works</Link>
          <Link to="/pricing">Pricing</Link>
          <Link to="/faq">Questions</Link>
          <Link to="/redeem">Use your voucher</Link>
        </div>
        <div>
          <h2>Policies</h2>
          <Link to="/accessibility">Accessibility</Link>
          <Link to="/privacy">Privacy</Link>
          <Link to="/ai-and-media-consent">Creative tools & media</Link>
          <Link to="/refunds">Refunds</Link>
        </div>
        <div>
          <h2>Need a hand?</h2>
          <a href="mailto:lifescapsule@jpwlabs.com">lifescapsule@jpwlabs.com</a>
          <p>Questions about access, privacy, or a book in progress.</p>
        </div>
      </div>
      <div className="shell footer__bottom">
        <span>© {new Date().getFullYear()} Life’s Capsule</span>
        <Link to="/terms">Terms</Link>
        <span>Private family storytelling, made with care.</span>
      </div>
    </footer>
  );
}

function RouteShell({
  children,
  pageOwnsChrome,
  announcePageChrome,
}: {
  children: React.ReactNode;
  pageOwnsChrome: boolean;
  announcePageChrome: (owned: boolean) => void;
}) {
  const location = useLocation();
  const auth = useAuthSession();
  // The book and step a page is on, for the making bar and Help (F1, F8).
  const [task, setTask] = useState<MakingTask | null>(null);
  const page = (
    <MakingTaskContext.Provider value={setTask}>
      <PageChromeContext.Provider value={announcePageChrome}>{children}</PageChromeContext.Provider>
    </MakingTaskContext.Provider>
  );
  // The Studio and the live demos keep their own top bar, which leads back to My Library.
  const isImmersive =
    location.pathname === "/app/demo" ||
    location.pathname === "/app/demo/classic" ||
    (studioPocPath !== null && location.pathname === studioPocPath) ||
    location.pathname.includes("/studio/");
  // Two chromes (F1): the public site's, and the one signed-in making chrome. A session being
  // restored (an expired sign-in with its refresh still kept) counts as signed in, so a returning
  // customer's home page doesn't show the visitor's header first and then jump (review P2-c).
  const signedIn = Boolean(auth.tokens) || auth.restoringSession;
  const chrome = shellChrome(location.pathname, signedIn);
  // A signed-out step into the site keeps its own brand-and-task header (W3 Rounds 10–11).
  const signedOutTask = signedOutTaskLabel(location.pathname, signedIn);
  // A redesigned page draws its own header and footer. The page keeps its place in the tree
  // either way, so handing the chrome over never remounts it.
  return (
    <>
      {pageOwnsChrome || isImmersive ? null : signedOutTask ? (
        <TaskHeader label={signedOutTask} />
      ) : chrome === "making" ? (
        // The same links on every signed-in page; the front door keeps its own colours (C2).
        <MakingChrome task={task} dark={location.pathname === "/"} />
      ) : (
        <PublicHeader />
      )}
      {page}
      {!pageOwnsChrome && publicSitePage(location.pathname) && (
        <Footer compact={location.pathname === "/"} />
      )}
    </>
  );
}

function StudioDemoRoute() {
  const [search] = useSearchParams();
  return search.get("view") === "classic" ? <StudioPage /> : <StudioFlagshipMock />;
}

function LegacyBookRedirect() {
  const { bookId = "" } = useParams();
  const location = useLocation();
  const chapterId = location.pathname.split("/chapters/")[1]?.split("/")[0];
  const storyPath = location.pathname.split("/story/")[1] ?? "";
  let phase = "gather";
  if (location.pathname.endsWith("/preview") || storyPath.startsWith("preview")) phase = "finish";
  else if (storyPath.startsWith("brief")) phase = "plan";
  else if (storyPath.startsWith("outline") || storyPath.startsWith("pages")) phase = "build";
  else if (storyPath.startsWith("illustrations") || storyPath.startsWith("audio")) phase = "design";
  else if (storyPath.startsWith("approvals") || storyPath.startsWith("proof")) phase = "review";
  else if (location.pathname.endsWith("/people") || location.pathname.endsWith("/settings"))
    phase = "plan";
  return (
    <Navigate
      replace
      to={`/app/books/${bookId}/studio/${phase}${chapterId ? `/${chapterId}` : ""}`}
    />
  );
}

function Loading() {
  return <PageLoading />;
}

export function RetiredBookToolRedirect() {
  const { bookId = "" } = useParams<{ bookId: string }>();
  return <Navigate replace to={`/app/books/${encodeURIComponent(bookId)}/tools`} />;
}

function ProtectedRoute({ children, returnTo }: { children: React.ReactNode; returnTo: string }) {
  const auth = useAuthSession();
  if (!auth.tokens) return <Navigate replace to="/sign-in" state={{ returnTo }} />;
  return children;
}

function PrivateReaderRoute() {
  const { bookId = "" } = useParams<{ bookId: string }>();
  const { search } = useLocation();
  return <Navigate replace to={legacyReadingHref(bookId, search)} />;
}

export function GuidedBedtimeRoute() {
  const { bookId = "" } = useParams<{ bookId: string }>();
  // Signing in again returns to the same guided page, with My Story's guided steps if they were on.
  // Only that switch is carried: anything else in the address stays out of the saved sign-in.
  const { search } = useLocation();
  const guidedStory = new URLSearchParams(search).has("guided-story") ? "?guided-story" : "";
  return (
    <ProtectedRoute returnTo={`/app/books/${encodeURIComponent(bookId)}/guided${guidedStory}`}>
      <StudioSessionObservation />
      {/* Keyed by book, so nothing remembered for one book carries over to another. */}
      <GuidedBedtimePage key={bookId} bookId={bookId} />
    </ProtectedRoute>
  );
}

function PrivateStorySearchRoute() {
  const { bookId = "" } = useParams<{ bookId: string }>();
  const { search } = useLocation();
  return <Navigate replace to={legacyReadingHref(bookId, search, true)} />;
}

export function App() {
  const motionRoot = useRef<HTMLDivElement>(null);
  const { pathname } = useLocation();
  const uiMode = useUiMode();
  const [pageAnnouncedChrome, setPageAnnouncedChrome] = useState(false);
  const redesigned = pageAnnouncedChrome || (uiMode === "next" && redesignOwnsPage(pathname));
  useEffect(() => {
    if (motionRoot.current) return installInterfaceMotion(motionRoot.current);
  }, []);
  return (
    <div
      ref={motionRoot}
      className="app"
      // "homepage" switches the app-wide motion off; the homepage and redesigned pages bring their own.
      data-motion-scope={pathname === "/" || redesigned ? "homepage" : "customer"}
    >
      <ScrollToTop />
      <RouteMetadata />
      <a
        className="skip-link"
        href="#main-content"
        onClick={(event) => {
          const main = document.getElementById("main-content");
          if (!main) return;
          event.preventDefault();
          main.tabIndex = -1;
          main.focus();
          main.scrollIntoView({ block: "start", behavior: "instant" });
        }}
      >
        Skip to main content
      </a>
      <RouteShell pageOwnsChrome={redesigned} announcePageChrome={setPageAnnouncedChrome}>
        <Suspense fallback={<Loading />}>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route
              path="/books"
              element={<Redesign next={<NextCollectionPage />} classic={<CatalogPage />} />}
            />
            <Route
              path="/demo"
              element={<Redesign next={<NextExampleHubPage />} classic={<DemoHubPage />} />}
            />
            <Route
              path="/demo/memoir"
              element={
                <Redesign
                  next={<RedesignRedirect to="/demo?book=story&sample=1" />}
                  classic={<MemoirDemoPage path="memoir" />}
                />
              }
            />
            {/* The finished Lantern and Fox book replaced the earlier 12-page proof sample. */}
            <Route path="/demo/bedtime" element={<Navigate replace to="/bedtime-story-demo" />} />
            <Route path="/demo/my-story-poc" element={<Navigate replace to="/demo/memoir" />} />
            <Route path="/demo/our-story-poc" element={<Navigate replace to="/demo/family" />} />
            <Route
              path="/demo/family"
              element={
                <Redesign
                  next={<RedesignRedirect to="/demo" />}
                  classic={<MemoirDemoPage path="family" />}
                />
              }
            />
            <Route path="/demo/phone-capture" element={<Navigate replace to="/demo" />} />
            <Route path="/demo/photo-restoration" element={<Navigate replace to="/demo" />} />
            <Route path="/demo/prompt-campaigns" element={<Navigate replace to="/demo" />} />
            <Route path="/demo/family-review" element={<Navigate replace to="/demo/family" />} />
            <Route
              path="/gift"
              element={
                <Redesign next={<VoucherQuestionsRedirect />} classic={<VoucherPurchasePage />} />
              }
            />
            <Route
              path="/books/:templateId"
              element={<Redesign next={<NextBookDetailsPage />} classic={<ProductPage />} />}
            />
            <Route
              path="/how-it-works"
              element={<Redesign next={<NextHowItWorksPage />} classic={<HowItWorksPage />} />}
            />
            <Route
              path="/bedtime-story-demo"
              element={
                <Redesign
                  next={<RedesignRedirect to="/demo?book=children&read=1" />}
                  classic={<BedtimeStoryDemoPage />}
                />
              }
            />
            <Route
              path="/qvc/bedtime-rehearsal"
              element={<Navigate replace to="/bedtime-story-demo" />}
            />
            <Route path="/resume" element={<QvcResumePage />} />
            <Route
              path="/pricing"
              element={<Redesign next={<VoucherQuestionsRedirect />} classic={<PricingPage />} />}
            />
            <Route
              path="/faq"
              element={<Redesign next={<NextFaqPage />} classic={<FaqPage />} />}
            />
            <Route
              path="/sign-in"
              element={<Redesign next={<NextSignInPage />} classic={<SignInPage />} />}
            />
            <Route
              path="/sign-in/complete"
              element={<Redesign next={<NextSignInCompletePage />} classic={<MagicSignInPage />} />}
            />
            <Route
              path="/app"
              element={<Redesign next={<NextLibraryPage />} classic={<AccountPage />} />}
            />
            <Route
              path="/app/account"
              element={<Redesign next={<NextAccountSettingsPage />} classic={<AccountPage />} />}
            />
            <Route
              path="/app/books/:bookId/settings"
              element={
                <Redesign
                  next={<NextBookSettingsPage />}
                  classic={<ClassicBookSettingsRedirect />}
                />
              }
            />
            <Route
              path="/support"
              element={
                <Redesign
                  next={<NextSupportPage />}
                  classic={
                    <ProtectedRoute returnTo="/support">
                      <ContactSupportPage />
                    </ProtectedRoute>
                  }
                />
              }
            />
            <Route
              path="/app/new"
              element={<Redesign next={<NextStartPage />} classic={<CreateExampleBookPage />} />}
            />
            <Route path="/app/photos" element={<PhotoLibraryPage />} />
            <Route
              path="/app/physical-orders/:physicalOrderId"
              element={
                <OneOrderPerPage>
                  <Redesign next={<NextOrderProgressPage />} classic={<PhysicalOrderPage />} />
                </OneOrderPerPage>
              }
            />
            <Route path="/app/books/:bookId/tools" element={<BookCapabilityHubPage />} />
            <Route path="/app/books/:bookId/production" element={<NarrativeProductionPage />} />
            <Route path="/app/books/:bookId/output" element={<ArrangeRoute />} />
            <Route
              path="/app/books/:bookId/print-review"
              element={
                <Redesign
                  next={<NextPrintReviewPage classic={<ExactPrintReviewPage />} />}
                  classic={<ExactPrintReviewPage />}
                />
              }
            />
            <Route path="/app/books/:bookId/guided" element={<GuidedBedtimeRoute />} />
            <Route
              path="/app/books/:bookId/print-simulation"
              element={<RetiredBookToolRedirect />}
            />
            <Route path="/app/books/:bookId/studio" element={<LegacyBookRedirect />} />
            <Route
              path="/app/books/:bookId/studio/:phase/:itemId?"
              element={
                <>
                  <StudioSessionObservation />
                  <StudioPage />
                </>
              }
            />
            {/* The family page is back under each book (E11, 2026-11-02), in either design. */}
            <Route path="/app/books/:bookId/family" element={<NextBookFamilyPage />} />
            <Route path="/app/books/:bookId/capture" element={<RetiredBookToolRedirect />} />
            <Route path="/app/books/:bookId/archive" element={<RetiredBookToolRedirect />} />
            <Route path="/app/books/:bookId/delivery" element={<RetiredBookToolRedirect />} />
            <Route path="/app/books/:bookId/memory-assets" element={<RetiredBookToolRedirect />} />
            <Route
              path="/app/books/:bookId/illustration-director"
              element={<RetiredBookToolRedirect />}
            />
            <Route
              path="/app/books/:bookId/interviews/:sessionId"
              element={<RetiredBookToolRedirect />}
            />
            <Route path="/app/books/:bookId/narration" element={<RetiredBookToolRedirect />} />
            <Route path="/app/books/:bookId/quality" element={<RetiredBookToolRedirect />} />
            <Route path="/app/books/:bookId/reorders" element={<RetiredBookToolRedirect />} />
            <Route path="/app/books/:bookId/reader" element={<PrivateReaderRoute />} />
            <Route path="/app/books/:bookId/family-map" element={<RetiredBookToolRedirect />} />
            <Route path="/app/books/:bookId/notifications" element={<RetiredBookToolRedirect />} />
            <Route path="/app/books/:bookId/search" element={<PrivateStorySearchRoute />} />
            <Route path="/app/book-templates" element={<Navigate replace to="/app" />} />
            <Route path={wave4RoutePatterns.memoryIndex} element={<RetiredBookToolRedirect />} />
            <Route path={wave4RoutePatterns.timeline} element={<RetiredBookToolRedirect />} />
            <Route path={wave4RoutePatterns.language} element={<RetiredBookToolRedirect />} />
            <Route path={wave4RoutePatterns.accessibility} element={<RetiredBookToolRedirect />} />
            <Route path={wave4RoutePatterns.bookType} element={<RetiredBookToolRedirect />} />
            <Route
              path="/app/books/:bookId/story/*"
              element={
                <>
                  <StudioSessionObservation />
                  <StoryWorkspacePage />
                </>
              }
            />
            <Route path="/app/books/:bookId/*" element={<LegacyBookRedirect />} />
            <Route path="/invitations/accept" element={<AcceptInvitationPage />} />
            <Route
              path="/support/approve"
              element={
                <Redesign next={<NextSupportApprovalPage />} classic={<SupportApprovalPage />} />
              }
            />
            <Route
              path="/unsubscribe"
              element={
                <Redesign next={<NextUnsubscribePage />} classic={<UnsubscribeReminderPage />} />
              }
            />
            <Route path="/listen/:shareToken" element={<ListenPage />} />
            <Route
              path="/redeem"
              element={<Redesign next={<NextRedeemPage />} classic={<RedeemPage />} />}
            />
            <Route path="/checkout/success" element={<CheckoutSuccessPage />} />
            <Route
              path="/checkout/:templateId"
              element={<Redesign next={<NextRetiredCheckoutPage />} classic={<CheckoutPage />} />}
            />
            <Route path="/app/demo" element={<StudioDemoRoute />} />
            <Route
              path="/app/demo/classic"
              element={<Navigate replace to="/app/demo?view=classic" />}
            />
            <Route path="/app/demo/diagnostics" element={<Navigate replace to="/app/demo" />} />
            {StudioPocPage && studioPocPath && (
              <Route path={studioPocPath} element={<StudioPocPage />} />
            )}
            {PipelineArtifactReviewLab && (
              <Route
                path="/app/labs/studio-artifact-review"
                element={<PipelineArtifactReviewLab />}
              />
            )}
            <Route path="/accessibility" element={<PolicyPage kind="accessibility" />} />
            <Route path="/privacy" element={<PolicyPage kind="privacy" />} />
            <Route path="/terms" element={<PolicyPage kind="terms" />} />
            <Route path="/refunds" element={<PolicyPage kind="refunds" />} />
            <Route path="/ai-and-media-consent" element={<PolicyPage kind="consent" />} />
            <Route
              path="*"
              element={<Redesign next={<NextRecoveryPage />} classic={<NotFoundPage />} />}
            />
          </Routes>
        </Suspense>
      </RouteShell>
    </div>
  );
}
